import React, { useState } from "react";
import {
  Box,
  InputAdornment,
  Modal,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  sendEmailVerification,
  signInWithCustomToken,
} from "firebase/auth";
import { ShowErrorToast, ShowSuccessToast } from "../../Toast/toast";
import {
  chooseModalEmailVerify,
  chooseModalLogin,
  closeChooseModal,
} from "../../../../Infrastructure/States/authModalsSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  ChooseAccBox,
  FormBox,
  Heading,
  MainBox,
  SignUpTextLink,
  StyledToggleButtonGroup,
  ToggleBtn,
  styles,
} from "./createAccountStyles";
import {
  StyledTextField,
  StyledIconButton,
} from "../../../../Infrastructure/Theme/styledComponents";
import { useLocation, useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import { httpsCallable } from "firebase/functions";
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import { functions, auth } from "../../../../Infrastructure/config";
import { useAuthValue } from "../../../../Infrastructure/States/authContext";
import { trackEvent } from "../../../../Infrastructure/Utilities/analytics";
import ButtonLoading from "../../Button/ButtonLoading";
import AnalyticsConstants from "../../../../Infrastructure/Constants/AnalyticsContants";
import ERROR_CONSTANTS from "../../../../Infrastructure/Constants/errorConstants";
import { getTimezone } from "../../../../Infrastructure/Utilities/getTimezone";
import { emailSchema } from "../../../../Infrastructure/Utilities/emailValidator";
import * as amplitude from "@amplitude/analytics-browser";
import paths from "../../../../Infrastructure/Router/paths";
import { UNAUTH_LOGS_CLIENT, USER_LOGS_CLIENT } from "../../../../Infrastructure/Constants/logsConstant";
import { safeClientLogEvent } from "../../../../Infrastructure/Utilities/safeClientLogEvent";

export const CreateAccount = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const isOpenModal = useSelector((state) => state.auth.isOpenModal);
  const [showPassword, setShowPassword] = useState(true);
  const [showRePassword, setShowRePassword] = useState(true);
  const [isEducator, setIsEducator] = useState(false);
  const { setTimeActive } = useAuthValue();
  const ip = useSelector((state) => state.ip.address)
  const userData = useSelector((state) => state.userData.data)
  const page = useSelector((state) => state.route.currentRoute);
  
  const handleCloseModal = () => {
    dispatch(closeChooseModal());
    formik.resetForm();
  };
  const handleLoginButtonClick = () => {
    dispatch(chooseModalLogin());
    formik.resetForm();
  };
  const handleChangeEducator = (event, newAlignment) => {
    if (newAlignment !== null) {
      setIsEducator(newAlignment);
    }
  };
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  const handleToggleRePasswordVisibility = () => {
    setShowRePassword(!showRePassword);
  };
  const handleRegistrationError = async (error, email) => {
    if (!error) {
      return;
    }
    let errorMessage = ERROR_CONSTANTS.GENERIC_ERROR_TOAST;
    switch (error.code) {
      case "auth/email-already-in-use":
        errorMessage = "Email already exists";
        break;
      case "auth/weak-password":
        errorMessage = "Weak password. Please choose a stronger one.";
        break;
      case "auth/user-token-expired":
        errorMessage = "User session expired. Please log in again.";
        dispatch(chooseModalLogin());
        break;
      default:
        if (error.message) {
          errorMessage = error.message;
        }
    }
    await safeClientLogEvent({
        ...UNAUTH_LOGS_CLIENT.ACCOUNT_CREATION_FAILED,
        uid: null,
        source: "createAccount.jsx",
        metadata:  { email },
        error: errorMessage || null
      });
    ShowErrorToast(errorMessage);
  };
  const formik = useFormik({
  initialValues: {
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    reEnterPassword: "",
    phoneNumber: "",
    isEducator: isEducator,
  },
  validationSchema: Yup.object({
    firstName: Yup.string().required("First Name"),
    lastName: Yup.string().required("Last Name"),
    email: emailSchema,
    password: Yup.string()
      .min(6, "Must be 6 characters")
        .matches(
          /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/,
          "Password must contain a special character"
        )
      .required("Password"),
    reEnterPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm Password."),
    phoneNumber: Yup.string().matches(/^[0-9]+$/, "Phone number must be digits only"),
  }),
  onSubmit: (values) => {
    setLoading(true);
    const { email, password, firstName, lastName, phoneNumber } = values;
    const timezone = getTimezone()
    if (!formik.isValid) {
      setLoading(false);
      return;
    }
    const restrictedEmails = process.env.REACT_APP_ADMIN_EMAIL?.split(',').map(e => e.trim()) || [];
    if (restrictedEmails.includes(email)) {
      safeClientLogEvent({
        uid: null,
        ...UNAUTH_LOGS_CLIENT.ADMIN_EMAIL_RESTRICTED,
        source: "createAccount",
        metadata: { email }
      });
      ShowErrorToast("Access denied. Admin is not authorized to create account.");
      setLoading(false)
      return;
    }
    const createUser = httpsCallable(functions, "createaccount");
    let token = null;
    let uid = null;
        setLoading(true);
        // Call Firebase Function to create user & get custom token
        createUser({
          email,
          password,
          firstName,
          lastName,
          number: phoneNumber,
          isEducator,
          timezone,
          ip: ip ?? null,
          page
        })
      .then((response) => {
        const { isSuccess, token: tokenReceived, uid: uidReceived, errorMessage } = response.data || {};
        if (!isSuccess || !tokenReceived) {
          throw new Error(errorMessage || "Account creation failed");
        }
        token = tokenReceived;
        uid = uidReceived;
        return signInWithCustomToken(auth, token);
      })
      .then(async (userCredential) => {
        const user = userCredential.user
        try {
          trackEvent(AnalyticsConstants.LEARNER_REGISTRATION_SUCCESS, {});
          safeClientLogEvent({
            uid,
            ...USER_LOGS_CLIENT.LEARNER_REGISTRATION_SUCCESS,
            source: "createAccount.jsx",
            metadata: { email }
          });
        }catch(trackingError) {
          safeClientLogEvent({
            uid,
            ...USER_LOGS_CLIENT.TRACKING_EVENT_FAILED,
            source: "createAccount.jsx",
            error: trackingError.message,
                    metadata: { event: AnalyticsConstants.LEARNER_REGISTRATION_SUCCESS }
          });
        }
        amplitude.setUserId(uid);
        const identifyObj = new amplitude.Identify().set("email", userData?.account?.email);
        amplitude.identify(identifyObj)
        dispatch(closeChooseModal())
        try {
          await sendEmailVerification(user)
          dispatch(chooseModalEmailVerify());
          ShowSuccessToast("Learner account created successfully! Please verify your email.");
          setTimeActive(true);
        }catch(error) {
          safeClientLogEvent({
            uid: user.uid,
            ...USER_LOGS_CLIENT.EMAIL_VERIFICATION_FAILED,
            source: "createAccount.jsx",
            error: error.message,
            metadata: { email: user.email }
          });
          throw new Error(USER_LOGS_CLIENT.EMAIL_VERIFICATION_FAILED.message);
        }})
      .catch((error) => {
        handleRegistrationError(error, values.email)
      }).finally(() => {
        setLoading(false);
      })
}});
  const handleEducatorButton = () => {
    navigate(paths.onboardingPage.path, { state: { from: location.pathname } });
    dispatch(closeChooseModal());
    formik.resetForm();
  }
  return (
      <Modal
        open={isOpenModal}
        disableScrollLock
        onClose={handleCloseModal}
        aria-labelledby="login-modal-title"
        aria-describedby="login-modal-description"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        BackdropProps={{
          sx: styles.backdrop,
        }}
      >
        <MainBox width= {{xs: "96%", sm: '400px', md: '450px'}}>
          <Box
            onClick={handleCloseModal}
            sx={{
              position: "relative",
              cursor:'pointer',
              top: "9px !important",
              display:'inline-block',
              right: {
                md: "-187px",
                sm: "-159px",
                xs: "-159px",
              },
            }}
          >
            <CloseIcon />
          </Box>
          <Heading variant="h1">Create Account</Heading>
          <Box>
            <SignUpTextLink variant="h6" onClick={handleLoginButtonClick}>
              Already have an account? Log In
            </SignUpTextLink>
          </Box>
          <FormBox component="form" onSubmit={formik.handleSubmit} noValidate>
            <ChooseAccBox>
              <StyledToggleButtonGroup
                color="primary"
                value={isEducator}
                exclusive
                onChange={handleChangeEducator}
                aria-label="Platform"
              >
                <ToggleBtn type="button" value={false}>
                  Learner Account
                </ToggleBtn>
                <ToggleBtn
                  type="button"
                  value={true}
                  onClick={handleEducatorButton}
                >
                  Educator Account
                </ToggleBtn>
              </StyledToggleButtonGroup>
            </ChooseAccBox>
            <StyledTextField
              name="firstName"
              label={
                formik.touched.firstName && Boolean(formik.errors.firstName)
                  ? `${formik.errors.firstName}`
                  : "First Name"
              }
              variant="standard"
              onChange={formik.handleChange}
              value={formik.values.firstName}
              error={
                formik.touched.firstName && Boolean(formik.errors.firstName)
              }
              fullWidth
            />
            <StyledTextField
              name="lastName"
              sx={{ mt: "6px" }}
              label={
                formik.touched.lastName && Boolean(formik.errors.lastName)
                  ? `${formik.errors.lastName}`
                  : "Last Name"
              }
              variant="standard"
              onChange={formik.handleChange}
              value={formik.values.lastName}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              fullWidth
            />
            <StyledTextField
              name="email"
              sx={{ mt: "6px" }}
              label={
                formik.touched.email && Boolean(formik.errors.email)
                  ? `${formik.errors.email}`
                  : "Email"
              }
              variant="standard"
              onChange={formik.handleChange}
              value={formik.values.email}
              error={formik.touched.email && Boolean(formik.errors.email)}
              fullWidth
            />
            <StyledTextField
              name="password"
              sx={{ mt: "6px" }}
              label={
                formik.touched.password && Boolean(formik.errors.password)
                  ? formik.errors.password
                  : "Password"
              }
              variant="standard"
              type={showPassword ? "password" : "text"}
              onChange={formik.handleChange}
              value={formik.values.password}
              autoComplete="new-password"
              error={formik.touched.password && Boolean(formik.errors.password)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <StyledIconButton onClick={handleTogglePasswordVisibility}>
                      {showPassword ? <VisibilityOff fontSize="small"/> : <Visibility fontSize="small"/>}
                    </StyledIconButton>
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
            <StyledTextField
              name="reEnterPassword"
              sx={{ mt: "6px" }}
              label={
                formik.touched.reEnterPassword &&
                  formik.values.password !== formik.values.reEnterPassword
                  ? "Passwords do not match"
                  : "Confirm Password"
              }
              variant="standard"
              type={showRePassword ? "password" : "text"}
              onChange={formik.handleChange}
              value={formik.values.reEnterPassword}
              autoComplete="new-Password"
              error={
                formik.touched.reEnterPassword &&
                formik.values.password !== formik.values.reEnterPassword
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <StyledIconButton onClick={handleToggleRePasswordVisibility}>
                      {showRePassword ? <VisibilityOff fontSize="small"/> : <Visibility fontSize="small"/>}
                    </StyledIconButton>
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
            <StyledTextField
              name="phoneNumber"
              sx={{ mt: "6px" }}
              label={
                formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)
                  ? `${formik.errors.phoneNumber}`
                  : "Phone Number (Optional)"
              }
              variant="standard"
              onChange={formik.handleChange}
              type="tel"
              autoComplete="tel"
              value={formik.values.phoneNumber}
              error={
                formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)
              }
              InputProps={{
                inputMode: "numeric",
                onKeyPress: (event) => {
                  if (isNaN(event.key)) {
                    event.preventDefault();
                  }
                },
              }}
              fullWidth
            />
            <Box pt={3}>
              <ButtonLoading text={"Create Account"} type={"submit"} loading={loading} />
            </Box>
          </FormBox>
        </MainBox>
      </Modal>
  );
}